import AppButton from "../components/common/input/AppButton";
import MainContainer from "../components/common/layout/MainContainer";
import PaymentDeniedIcon from "../components/common/svg/PaymentDeniedIcon";
import PaymentSuccessIcon from "../components/common/svg/PaymentSuccessIcon";
import { usePaymentStatusPolling } from "../hooks/usePaymentStatusPolling";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Brands } from "../models/brands";
import { useDispatch } from "react-redux";
import { setCheckoutDetails } from "../store/checkoutSlice";
import { useLazyGetCheckoutIdQuery } from "../app/services/api";
import { useAppSelector } from "../app/hooks";
import { formatCurrency } from "../utils/common/numberUtils";
import LoadingScreen from "../components/common/layout/LoadingScreen";
import { useMemo } from "react";

export type StatusContent = {
    primaryButtonText?: string;
    secondaryButtonText?: string;
    primaryButtonClick?: () => void;
    secondaryButtonClick?: () => void;
    icon?: React.ReactNode;
    message?: React.ReactNode;
    title?: string;
};

const PaymentProcessing = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { checkoutDetails } = useAppSelector((state) => state.checkout);
    const { publicProfile, publicTeam } = useAppSelector((state) => state.profile,);
    const [getCheckoutIdTrigger] = useLazyGetCheckoutIdQuery();

    const uniqueId = publicProfile?.id ?? publicTeam?.id;
    const tipValue = checkoutDetails?.tipValue;
    const currency = checkoutDetails?.currency;
    const checkoutId = searchParams.get("checkoutId") || "";
    const brandIdParam = searchParams.get('brandId');
    const tipptingTo = publicProfile?.firstName ?? publicTeam?.name;
    const tippingAmount = tipValue != null && currency ? formatCurrency(tipValue, currency) : '';
    const { paymentStatus } = usePaymentStatusPolling({checkoutId, isPolling: true});

    const isBrand = (value: string | null): value is string => {
        if (value === null) {
            return false;
        }

        const numericValue = Number(value);

        return !Number.isNaN(numericValue) && Brands[numericValue] !== undefined;
    };

    const brandId = isBrand(brandIdParam) ? Number(brandIdParam) as Brands : undefined;

    const handleRetry = async () => {
        if (!checkoutDetails) {
            navigate('/payment', { replace: true });
            return;
        }

        try {
            const {
                checkoutId,
                ...checkoutRequestBody
            } = checkoutDetails;

            const res = await getCheckoutIdTrigger(checkoutRequestBody).unwrap();

            dispatch(
                setCheckoutDetails({
                    ...checkoutRequestBody,
                    checkoutId: res.content,
                }),
            );

            navigate('/payment', { replace: true });
        } catch (error) {
            console.error('Failed to retry payment', error);
        }

        navigate('/payment', { replace: true });
    };

    const statusContent = useMemo<StatusContent>(() => {
        switch (paymentStatus) {
            case "success":
                return {
                    primaryButtonText: "Send another tip",
                    title: "Payment successful!",
                    message: (
                        <p>
                            Thank you for your support! You tipped{" "}
                            <span className="font-semibold">{tippingAmount}</span> to {tipptingTo}.{" "}
                            (#{uniqueId}). They'll see your tip right away.
                        </p>
                    ),
                    icon: <PaymentSuccessIcon />,
                    primaryButtonClick: () => {
                        navigate("/");
                    },
                };

            case "denied":
                return {
                    primaryButtonText: "Try again",
                    secondaryButtonText: "Contact support",
                    title: "Payment failed",
                    message: (
                        <p>
                            We couldn't process your{" "}
                            <span className="font-semibold">{tippingAmount}</span> tip for {tipptingTo}.{" "}
                            Please try again or check your payment details.
                        </p>
                    ),
                    icon: <PaymentDeniedIcon />,
                    primaryButtonClick: handleRetry,
                    secondaryButtonClick: () => {
                        window.location.href = "https://paynt.com/contact-us/";
                    },
                };

            case "processing":
            default:
                return {
                    title: "Payment processing...",
                    icon: <LoadingScreen />,
                };
        }
    }, [paymentStatus, tippingAmount, tipptingTo, uniqueId, navigate, handleRetry]);

    return (
        <MainContainer brandId={brandId} className="flex flex-col justify-between min-h-auto pt-[190px] px-5 pb-10 max-md:h-[100dvh] max-md:min-h-0">
            <div>
                <div className="flex items-center justify-center">
                    {statusContent.icon}
                </div>
                <h3 className="mt-10 mb-[22px] text-[22px] font-medium leading-7 text-center">{statusContent.title}</h3>
                <p className="text-center">{statusContent.message}</p>
            </div>

            {
                statusContent.primaryButtonText ?
                    <div className="flex flex-col gap-4">
                        <AppButton
                            className="mt-2"
                            sizeClassName="w-full h-12"
                            branding={brandId}
                            value={statusContent.primaryButtonText}
                            onClick={statusContent.primaryButtonClick}
                        />
                        {
                            statusContent.secondaryButtonText ?
                            <AppButton
                                className="mt-2"
                                sizeClassName="w-full h-12"
                                branding={brandId}
                                mode="secondary"
                                value={statusContent.secondaryButtonText}
                                onClick={statusContent.secondaryButtonClick}
                            /> : null
                        }
                    </div> : null
            }
        </MainContainer>
    );
};

export default PaymentProcessing;